import { checkAndUpdateToken } from "./auth";
import { BACKEND_URL_PREFIX } from "../utils/const";
import { buildDefaultHeaders } from "./helper";
import { createNotification } from "../components/notifications/notifications";
import * as types from './types';
import { setObjects, setSelectedObject, OBJECT_TYPES, refreshAreas, refreshImage } from './infoTerminal';

// Генерация уникального ID (для локальных операций до синхронизации с сервером)
const generateId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

// ============ TREE LOADING ============

/**
 * Преобразование nested-дерева с сервера в плоский список для Redux
 */
const flattenTree = (nodes, parentId = null) => {
  let result = [];
  nodes.forEach(node => {
    const { children, ...nodeData } = node;
    result.push({
      ...nodeData,
      parentId: parentId,
      // Приводим поля к формату фронта
      sortOrder: node.sort_order || 0,
      isActive: node.is_active !== false,
      image: null, // Изображения загружаются отдельно
      areas: node.areas || []
    });
    if (children && children.length > 0) {
      result = result.concat(flattenTree(children, node.id));
    }
  });
  return result;
};

/**
 * GET /info_kiosk/scheme/objects/tree
 * Загрузка дерева объектов с сервера
 */
export const fetchObjectsTree = () => async (dispatch, getState) => {
  await dispatch(checkAndUpdateToken());
  if (!getState().auth.token) return;

  dispatch({ type: types.SET_INFO_TERMINAL_TREE_LOADING, payload: true });
  try {
    const response = await fetch(
      `${BACKEND_URL_PREFIX}/info_kiosk/scheme/objects/tree`,
      buildDefaultHeaders(getState)
    );
    if (response.ok) {
      const data = await response.json();
      const flatObjects = flattenTree(data);
      dispatch(setObjects(flatObjects));
    } else {
      createNotification('error', 'Ошибка загрузки дерева объектов');
    }
  } catch (err) {
    createNotification('error', 'Ошибка сети');
  } finally {
    dispatch({ type: types.SET_INFO_TERMINAL_TREE_LOADING, payload: false });
  }
};

// ============ AVAILABLE BUILDINGS (УМБ) ============

/**
 * GET /info_kiosk/scheme/available_buildings
 * Загрузка справочника зданий УМБ
 * @param {number|null} includeId - tfoid здания для включения (при редактировании)
 */
export const fetchAvailableBuildings = (includeId = null) => async (dispatch, getState) => {
  await dispatch(checkAndUpdateToken());
  if (!getState().auth.token) return;

  dispatch({ type: types.SET_INFO_TERMINAL_AVAILABLE_BUILDINGS_LOADING, payload: true });
  try {
    let url = `${BACKEND_URL_PREFIX}/info_kiosk/scheme/available_buildings`;
    if (includeId) {
      url += `?include_id=${includeId}`;
    }
    const response = await fetch(url, buildDefaultHeaders(getState));
    if (response.ok) {
      const data = await response.json();
      dispatch({ type: types.SET_INFO_TERMINAL_AVAILABLE_BUILDINGS, payload: data });
    } else {
      createNotification('error', 'Ошибка загрузки справочника зданий');
    }
  } catch (err) {
    createNotification('error', 'Ошибка сети');
  } finally {
    dispatch({ type: types.SET_INFO_TERMINAL_AVAILABLE_BUILDINGS_LOADING, payload: false });
  }
};

// ============ AVAILABLE ROOMS (Аудиторный фонд) ============

/**
 * GET /info_kiosk/scheme/available_rooms_by_building/<tfoid>
 * Загрузка аудиторий по зданию
 * @param {number} tfoid - ID здания в УМБ
 * @param {number|null} includeId - rid аудитории для включения (при редактировании)
 * @param {number|null} floor - фильтр по этажу
 */
export const fetchAvailableRoomsByBuilding = (tfoid, includeId = null, floor = null) => async (dispatch, getState) => {
  await dispatch(checkAndUpdateToken());
  if (!getState().auth.token) return;

  dispatch({ type: types.SET_INFO_TERMINAL_AVAILABLE_ROOMS_LOADING, payload: true });
  try {
    let url = `${BACKEND_URL_PREFIX}/info_kiosk/scheme/available_rooms_by_building/${tfoid}`;
    const params = new URLSearchParams();
    if (includeId) params.append('include_id', includeId);
    if (floor) params.append('floor', floor);
    if (params.toString()) url += `?${params.toString()}`;

    const response = await fetch(url, buildDefaultHeaders(getState));
    if (response.ok) {
      const data = await response.json();
      dispatch({ type: types.SET_INFO_TERMINAL_AVAILABLE_ROOMS, payload: data });
    } else {
      createNotification('error', 'Ошибка загрузки аудиторий');
    }
  } catch (err) {
    createNotification('error', 'Ошибка сети');
  } finally {
    dispatch({ type: types.SET_INFO_TERMINAL_AVAILABLE_ROOMS_LOADING, payload: false });
  }
};

/**
 * Очистка списка аудиторий
 */
export const clearAvailableRooms = () => ({
  type: types.SET_INFO_TERMINAL_AVAILABLE_ROOMS,
  payload: []
});

// ============ CRUD OPERATIONS ============

/**
 * POST /info_kiosk/scheme/objects
 * Создание объекта на сервере
 */
export const createObjectOnServer = (objectData) => async (dispatch, getState) => {
  await dispatch(checkAndUpdateToken());
  if (!getState().auth.token) return null;

  dispatch({ type: types.SET_INFO_TERMINAL_SAVING, payload: true });
  try {
    const response = await fetch(`${BACKEND_URL_PREFIX}/info_kiosk/scheme/objects`, {
      method: 'POST',
      body: JSON.stringify(objectData),
      ...buildDefaultHeaders(getState)
    });
    if (response.ok) {
      const created = await response.json();
      return created;
    } else {
      const errorData = await response.json();
      createNotification('error', errorData.error || 'Ошибка создания объекта');
      return null;
    }
  } catch (err) {
    createNotification('error', 'Ошибка сети');
    return null;
  } finally {
    dispatch({ type: types.SET_INFO_TERMINAL_SAVING, payload: false });
  }
};

/**
 * PUT /info_kiosk/scheme/objects/<oid>
 * Обновление объекта на сервере
 */
export const updateObjectOnServer = (oid, objectData) => async (dispatch, getState) => {
  await dispatch(checkAndUpdateToken());
  if (!getState().auth.token) return null;

  dispatch({ type: types.SET_INFO_TERMINAL_SAVING, payload: true });
  try {
    const response = await fetch(`${BACKEND_URL_PREFIX}/info_kiosk/scheme/objects/${oid}`, {
      method: 'PUT',
      body: JSON.stringify(objectData),
      ...buildDefaultHeaders(getState)
    });
    if (response.ok) {
      const updated = await response.json();
      return updated;
    } else {
      const errorData = await response.json();
      createNotification('error', errorData.error || 'Ошибка обновления объекта');
      return null;
    }
  } catch (err) {
    createNotification('error', 'Ошибка сети');
    return null;
  } finally {
    dispatch({ type: types.SET_INFO_TERMINAL_SAVING, payload: false });
  }
};

/**
 * DELETE /info_kiosk/scheme/objects/<oid>
 * Удаление объекта на сервере
 */
export const deleteObjectOnServer = (oid) => async (dispatch, getState) => {
  await dispatch(checkAndUpdateToken());
  if (!getState().auth.token) return false;

  dispatch({ type: types.SET_INFO_TERMINAL_SAVING, payload: true });
  try {
    const response = await fetch(`${BACKEND_URL_PREFIX}/info_kiosk/scheme/objects/${oid}`, {
      method: 'DELETE',
      ...buildDefaultHeaders(getState)
    });
    if (response.ok) {
      return true;
    } else {
      const errorData = await response.json();
      createNotification('error', errorData.error || 'Ошибка удаления объекта');
      return false;
    }
  } catch (err) {
    createNotification('error', 'Ошибка сети');
    return false;
  } finally {
    dispatch({ type: types.SET_INFO_TERMINAL_SAVING, payload: false });
  }
};

/**
 * Удаление объекта с отправкой запроса на сервер
 * Сначала удаляет на сервере, затем локально
 * @param {string|number} id - ID объекта для удаления
 */
export const deleteObject = (id) => async (dispatch, getState) => {
  const { objects } = getState().infoTerminal;
  const object = objects.find(obj => obj.id === id);

  if (!object) {
    createNotification('error', 'Объект не найден');
    return false;
  }

  // Отправляем DELETE запрос на сервер
  const success = await dispatch(deleteObjectOnServer(id));

  if (success) {
    // Удаляем локально из Redux
    dispatch({ type: types.DELETE_INFO_TERMINAL_OBJECT, payload: id });
    createNotification('success', `Объект "${object.name}" удалён`);
    return true;
  }

  return false;
};

// ============ BUILDING FROM DIRECTORY ============

/**
 * Добавление здания из справочника УМБ
 * Этажи пользователь создаёт сам, но ограничены значением floors_normalized
 * @param {string|number} parentId - ID родительской территории
 * @param {object} buildingData - Данные здания из справочника
 */
export const addBuildingFromDirectory = (parentId, buildingData) => async (dispatch, getState) => {
  const { objects } = getState().infoTerminal;
  const parent = objects.find(obj => obj.id === parentId);

  if (!parent || parent.type !== 'territory') {
    createNotification('error', 'Здание можно добавить только в территорию');
    return null;
  }

  const siblings = objects.filter(obj => obj.parentId === parentId);
  const maxFloors = Math.max(1, buildingData.floors_normalized || 1);

  // Создаем здание на сервере
  const buildingPayload = {
    type: 'building',
    parent_id: parentId,
    name: buildingData.short_title || buildingData.full_title,
    description: buildingData.address || '',
    sort_order: siblings.length + 1,
    is_active: true,
    external_building_tfoid: buildingData.tfoid,
    max_floors: maxFloors
  };

  const createdBuilding = await dispatch(createObjectOnServer(buildingPayload));

  if (createdBuilding) {
    createNotification('success', `Здание "${buildingPayload.name}" добавлено (макс. ${maxFloors} этаж.)`);

    // Перезагружаем дерево
    await dispatch(fetchObjectsTree());

    // Выбираем созданное здание
    dispatch(setSelectedObject(createdBuilding.id));

    // Обновляем список доступных зданий
    dispatch(fetchAvailableBuildings());
  }

  return createdBuilding;
};

// ============ ROOM FROM DIRECTORY ============

/**
 * Добавление аудитории из справочника аудиторного фонда
 * @param {string|number} parentId - ID родительского этажа
 * @param {object} roomData - Данные аудитории из справочника
 */
export const addRoomFromDirectory = (parentId, roomData) => async (dispatch, getState) => {
  const { objects } = getState().infoTerminal;
  const parent = objects.find(obj => obj.id === parentId);

  if (!parent || parent.type !== 'floor') {
    createNotification('error', 'Аудиторию можно добавить только на этаж');
    return null;
  }

  const siblings = objects.filter(obj => obj.parentId === parentId);

  // Создаем аудиторию на сервере
  const roomPayload = {
    type: 'room',
    parent_id: parentId,
    name: roomData.short_name || roomData.name,
    description: roomData.type_title || '',
    sort_order: siblings.length + 1,
    is_active: true,
    external_room_id: roomData.rid
  };

  const createdRoom = await dispatch(createObjectOnServer(roomPayload));

  if (createdRoom) {
    createNotification('success', `Аудитория "${roomPayload.name}" добавлена`);

    // Перезагружаем дерево
    await dispatch(fetchObjectsTree());

    // Выбираем созданную аудиторию
    dispatch(setSelectedObject(createdRoom.id));
  }

  return createdRoom;
};

// ============ FLOOR VALIDATION ============

/**
 * Проверка возможности добавления этажа
 * @param {string|number} buildingId - ID здания
 * @returns {object} { valid: boolean, message?: string, currentFloors?: number, maxFloors?: number }
 */
export const canAddFloor = (buildingId) => (dispatch, getState) => {
  const { objects } = getState().infoTerminal;
  const building = objects.find(obj => obj.id === buildingId);

  if (!building || building.type !== 'building') {
    return { valid: false, message: 'Объект не является зданием' };
  }

  // Получаем данные о этажности из max_floors или external_building
  const maxFloors = building.max_floors || building.external_building?.floors_normalized || 99;

  const currentFloors = objects.filter(
    obj => obj.parentId === buildingId && obj.type === 'floor'
  ).length;

  if (currentFloors >= maxFloors) {
    return {
      valid: false,
      message: `Достигнут лимит этажей (${maxFloors}). Удалите существующий этаж или выберите другое здание.`,
      currentFloors,
      maxFloors
    };
  }

  return {
    valid: true,
    currentFloors,
    maxFloors,
    availableFloors: maxFloors - currentFloors
  };
};

/**
 * Добавление этажа с валидацией
 * @param {string|number} buildingId - ID здания
 */
export const addFloorWithValidation = (buildingId) => async (dispatch, getState) => {
  const validation = dispatch(canAddFloor(buildingId));

  if (!validation.valid) {
    createNotification('warning', validation.message);
    return null;
  }

  const { objects } = getState().infoTerminal;
  const siblings = objects.filter(obj => obj.parentId === buildingId && obj.type === 'floor');
  const floorNumber = siblings.length + 1;

  const floorPayload = {
    type: 'floor',
    parent_id: buildingId,
    name: `новый этаж`,
    description: '',
    sort_order: floorNumber,
    is_active: true
  };

  const createdFloor = await dispatch(createObjectOnServer(floorPayload));

  if (createdFloor) {
    createNotification('success', `Этаж ${floorNumber} добавлен`);
    await dispatch(fetchObjectsTree());
    dispatch(setSelectedObject(createdFloor.id));
  }

  return createdFloor;
};

// ============ TERRITORY (ручной ввод) ============

/**
 * Добавление территории (ручной ввод, как раньше)
 * @param {string} name - Название территории
 */
export const addTerritory = (name = 'Новая территория') => async (dispatch, getState) => {
  const { objects } = getState().infoTerminal;
  const territories = objects.filter(obj => obj.type === 'territory' && obj.parentId === null);

  const territoryPayload = {
    type: 'territory',
    parent_id: null,
    name,
    description: '',
    sort_order: territories.length + 1,
    is_active: true
  };

  const createdTerritory = await dispatch(createObjectOnServer(territoryPayload));

  if (createdTerritory) {
    createNotification('success', `Территория "${name}" создана`);
    await dispatch(fetchObjectsTree());
    dispatch(setSelectedObject(createdTerritory.id));
  }

  return createdTerritory;
};

// ============ HELPER: Get Building tfoid from Floor ============

/**
 * Получение tfoid здания-предка для этажа/аудитории
 * @param {string|number} objectId - ID объекта (этаж или аудитория)
 */
export const getBuildingTfoidForObject = (objectId) => (dispatch, getState) => {
  const { objects } = getState().infoTerminal;

  const findBuildingAncestor = (id) => {
    const obj = objects.find(o => o.id === id);
    if (!obj) return null;
    if (obj.type === 'building') return obj;
    if (obj.parentId) return findBuildingAncestor(obj.parentId);
    return null;
  };

  const building = findBuildingAncestor(objectId);
  return building?.external_building_tfoid || building?.external_building?.tfoid || null;
};

// ============ SAVE ALL CHANGES ============

/**
 * Сохранение всех изменений на сервере
 * Отправляет PUT запрос для каждого изменённого объекта и области
 * Использует PUT /info_kiosk/scheme/objects/<oid> и PUT /info_kiosk/scheme/areas/<aid>
 */
export const saveAllChanges = () => async (dispatch, getState) => {
  await dispatch(checkAndUpdateToken());
  if (!getState().auth.token) return false;

  const { objects, hasChanges, modifiedObjectIds, pendingAreaUpdates } = getState().infoTerminal;

  const hasObjectChanges = modifiedObjectIds.size > 0;
  const hasAreaChanges = Object.keys(pendingAreaUpdates).length > 0;

  if (!hasChanges || (!hasObjectChanges && !hasAreaChanges)) {
    createNotification('info', 'Нет изменений для сохранения');
    return true;
  }

  dispatch({ type: types.SET_INFO_TERMINAL_SAVING, payload: true });

  try {
    let successCount = 0;
    let errorCount = 0;

    // Сохраняем изменённые объекты
    if (hasObjectChanges) {
      const modifiedObjects = objects.filter(obj => modifiedObjectIds.has(obj.id));

      for (const obj of modifiedObjects) {
        const payload = {
          name: obj.name,
          description: obj.description || '',
          sort_order: obj.sortOrder || 0,
          is_active: obj.isActive !== false
        };

        if (obj.type === 'building' && obj.external_building_tfoid) {
          payload.external_building_tfoid = obj.external_building_tfoid;
        }
        if (obj.type === 'room' && obj.external_room_id) {
          payload.external_room_id = obj.external_room_id;
        }

        try {
          const response = await fetch(`${BACKEND_URL_PREFIX}/info_kiosk/scheme/objects/${obj.id}`, {
            method: 'PUT',
            body: JSON.stringify(payload),
            ...buildDefaultHeaders(getState)
          });

          if (response.ok) {
            successCount++;
          } else {
            errorCount++;
            const errorData = await response.json();
            console.error(`Ошибка сохранения объекта ${obj.id}:`, errorData);
          }
        } catch (err) {
          errorCount++;
          console.error(`Ошибка сети при сохранении объекта ${obj.id}:`, err);
        }
      }
    }

    // Сохраняем изменённые области (привязки)
    if (hasAreaChanges) {
      for (const [areaId, updates] of Object.entries(pendingAreaUpdates)) {
        try {
          const response = await fetch(`${BACKEND_URL_PREFIX}/info_kiosk/scheme/areas/${areaId}`, {
            method: 'PUT',
            body: JSON.stringify(updates),
            ...buildDefaultHeaders(getState)
          });

          if (response.ok) {
            successCount++;
          } else {
            errorCount++;
            const errorData = await response.json();
            console.error(`Ошибка сохранения области ${areaId}:`, errorData);
          }
        } catch (err) {
          errorCount++;
          console.error(`Ошибка сети при сохранении области ${areaId}:`, err);
        }
      }
    }

    if (errorCount === 0) {
      dispatch({ type: types.SET_INFO_TERMINAL_HAS_CHANGES, payload: false });
      // Обновляем детали объекта чтобы показать сохранённые привязки
      dispatch(refreshAreas());
      createNotification('success', 'Изменения сохранены');
      return true;
    } else if (successCount > 0) {
      createNotification('warning', `Сохранено ${successCount}, ошибок: ${errorCount}`);
      await dispatch(fetchObjectsTree());
      return false;
    } else {
      createNotification('error', 'Ошибка сохранения');
      return false;
    }
  } catch (err) {
    createNotification('error', 'Ошибка сети');
    return false;
  } finally {
    dispatch({ type: types.SET_INFO_TERMINAL_SAVING, payload: false });
  }
};

/**
 * Сброс изменений - перезагрузка данных с сервера
 */
export const discardChanges = () => async (dispatch) => {
  await dispatch(fetchObjectsTree());
  dispatch({ type: types.SET_INFO_TERMINAL_HAS_CHANGES, payload: false });
  createNotification('info', 'Изменения отменены');
};

// ============ IMAGES ============

/**
 * POST /info_kiosk/scheme/objects/<oid>/image
 * Загрузка изображения (backend самостоятельно формирует preview)
 * @param {number} objectId - ID объекта
 * @param {File} originalFile - Оригинальный файл изображения
 */
export const uploadImageToServer = (objectId, originalFile) => async (dispatch, getState) => {
  await dispatch(checkAndUpdateToken());
  if (!getState().auth.token) return null;

  dispatch({ type: types.SET_INFO_TERMINAL_SAVING, payload: true });

  try {
    const formData = new FormData();
    // Извлекаем расширение и используем безопасное имя без кириллицы
    const getExt = (name) => { const i = name.lastIndexOf('.'); return i > 0 ? name.slice(i + 1).toLowerCase() : ''; };
    const origExt = getExt(originalFile.name);
    formData.append('original', originalFile, origExt ? `image.${origExt}` : 'image');

    const response = await fetch(`${BACKEND_URL_PREFIX}/info_kiosk/scheme/objects/${objectId}/image`, {
      method: 'POST',
      body: formData,
      headers: {
        'Authorization': buildDefaultHeaders(getState).headers['Authorization']
      }
    });

    if (response.ok) {
      const imageData = await response.json();
      createNotification('success', 'Изображение загружено');
      // Перезагружаем дерево для обновления has_image
      await dispatch(fetchObjectsTree());
      // Уведомляем SchemaEditor о замене изображения
      dispatch(refreshImage());
      return imageData;
    } else {
      const errorData = await response.json();
      createNotification('error', errorData.error || 'Ошибка загрузки изображения');
      return null;
    }
  } catch (err) {
    createNotification('error', 'Ошибка сети при загрузке изображения');
    return null;
  } finally {
    dispatch({ type: types.SET_INFO_TERMINAL_SAVING, payload: false });
  }
};

/**
 * DELETE /info_kiosk/scheme/objects/<oid>/image
 * Удаление изображения
 * @param {number} objectId - ID объекта
 */
export const deleteImageFromServer = (objectId) => async (dispatch, getState) => {
  await dispatch(checkAndUpdateToken());
  if (!getState().auth.token) return false;

  dispatch({ type: types.SET_INFO_TERMINAL_SAVING, payload: true });

  try {
    const response = await fetch(`${BACKEND_URL_PREFIX}/info_kiosk/scheme/objects/${objectId}/image`, {
      method: 'DELETE',
      ...buildDefaultHeaders(getState)
    });

    if (response.ok) {
      createNotification('success', 'Изображение удалено');
      await dispatch(fetchObjectsTree());
      dispatch(refreshImage());
      return true;
    } else {
      const errorData = await response.json();
      createNotification('error', errorData.error || 'Ошибка удаления изображения');
      return false;
    }
  } catch (err) {
    createNotification('error', 'Ошибка сети');
    return false;
  } finally {
    dispatch({ type: types.SET_INFO_TERMINAL_SAVING, payload: false });
  }
};

/**
 * GET /info_kiosk/scheme/objects/<oid>/image/original
 * Получение URL оригинального изображения
 * @param {number} objectId - ID объекта
 * @param {string} uploadedAt - timestamp для cache-busting
 */
export const getImageOriginalUrl = (objectId, uploadedAt = '') => {
  const cacheBust = uploadedAt ? `?v=${encodeURIComponent(uploadedAt)}` : '';
  return `${BACKEND_URL_PREFIX}/info_kiosk/scheme/objects/${objectId}/image/original${cacheBust}`;
};

/**
 * GET /info_kiosk/scheme/objects/<oid>/image/preview
 * Получение URL preview изображения
 */
export const getImagePreviewUrl = (objectId, uploadedAt = '') => {
  const cacheBust = uploadedAt ? `?v=${encodeURIComponent(uploadedAt)}` : '';
  return `${BACKEND_URL_PREFIX}/info_kiosk/scheme/objects/${objectId}/image/preview${cacheBust}`;
};

/**
 * Загрузка изображения как blob для отображения (нужна авторизация)
 * @param {number} objectId - ID объекта
 * @param {string} type - 'original' или 'preview'
 */
export const fetchImageBlob = (objectId, type = 'original') => async (dispatch, getState) => {
  await dispatch(checkAndUpdateToken());
  if (!getState().auth.token) return null;

  try {
    const headers = buildDefaultHeaders(getState);
    const response = await fetch(
      `${BACKEND_URL_PREFIX}/info_kiosk/scheme/objects/${objectId}/image/${type}`,
      { ...headers, cache: 'no-store' }
    );

    if (response.ok) {
      const blob = await response.blob();
      return URL.createObjectURL(blob);
    }
    return null;
  } catch (err) {
    console.error('Ошибка загрузки изображения:', err);
    return null;
  }
};

// ============ AREAS (POLYGONS) ============

/**
 * POST /info_kiosk/scheme/objects/<oid>/areas
 * Создание области (полигона)
 * @param {number} objectId - ID объекта
 * @param {Array} polygon - Массив точек [[x,y], ...] в нормализованных координатах [0..1]
 * @param {number|null} targetObjectId - ID дочернего объекта для перехода
 * @param {string} label - Подпись для тултипа
 */
export const createAreaOnServer = (objectId, polygon, targetObjectId = null, label = '') => async (dispatch, getState) => {
  await dispatch(checkAndUpdateToken());
  if (!getState().auth.token) return null;

  dispatch({ type: types.SET_INFO_TERMINAL_SAVING, payload: true });

  try {
    const response = await fetch(`${BACKEND_URL_PREFIX}/info_kiosk/scheme/objects/${objectId}/areas`, {
      method: 'POST',
      body: JSON.stringify({
        polygon,
        target_object_id: targetObjectId,
        label,
        is_active: true
      }),
      ...buildDefaultHeaders(getState)
    });

    if (response.ok) {
      const areaData = await response.json();
      createNotification('success', 'Область создана');
      return areaData;
    } else {
      const errorData = await response.json();
      createNotification('error', errorData.error || 'Ошибка создания области');
      return null;
    }
  } catch (err) {
    createNotification('error', 'Ошибка сети');
    return null;
  } finally {
    dispatch({ type: types.SET_INFO_TERMINAL_SAVING, payload: false });
  }
};

/**
 * PUT /info_kiosk/scheme/areas/<aid>
 * Редактирование области
 * @param {number} areaId - ID области
 * @param {object} updates - Обновления { polygon, target_object_id, label, is_active }
 */
export const updateAreaOnServer = (areaId, updates) => async (dispatch, getState) => {
  await dispatch(checkAndUpdateToken());
  if (!getState().auth.token) return null;

  dispatch({ type: types.SET_INFO_TERMINAL_SAVING, payload: true });

  try {
    const payload = {};
    if (updates.polygon) payload.polygon = updates.polygon;
    if (updates.target_object_id !== undefined) payload.target_object_id = updates.target_object_id;
    if (updates.label !== undefined) payload.label = updates.label;
    if (updates.is_active !== undefined) payload.is_active = updates.is_active;

    const response = await fetch(`${BACKEND_URL_PREFIX}/info_kiosk/scheme/areas/${areaId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
      ...buildDefaultHeaders(getState)
    });

    if (response.ok) {
      const areaData = await response.json();
      return areaData;
    } else {
      const errorData = await response.json();
      createNotification('error', errorData.error || 'Ошибка обновления области');
      return null;
    }
  } catch (err) {
    createNotification('error', 'Ошибка сети');
    return null;
  } finally {
    dispatch({ type: types.SET_INFO_TERMINAL_SAVING, payload: false });
  }
};

/**
 * DELETE /info_kiosk/scheme/areas/<aid>
 * Удаление области
 * @param {number} areaId - ID области
 */
export const deleteAreaFromServer = (areaId) => async (dispatch, getState) => {
  await dispatch(checkAndUpdateToken());
  if (!getState().auth.token) return false;

  dispatch({ type: types.SET_INFO_TERMINAL_SAVING, payload: true });

  try {
    const response = await fetch(`${BACKEND_URL_PREFIX}/info_kiosk/scheme/areas/${areaId}`, {
      method: 'DELETE',
      ...buildDefaultHeaders(getState)
    });

    if (response.ok) {
      createNotification('success', 'Область удалена');
      return true;
    } else {
      const errorData = await response.json();
      createNotification('error', errorData.error || 'Ошибка удаления области');
      return false;
    }
  } catch (err) {
    createNotification('error', 'Ошибка сети');
    return false;
  } finally {
    dispatch({ type: types.SET_INFO_TERMINAL_SAVING, payload: false });
  }
};

/**
 * GET /info_kiosk/scheme/objects/<oid>
 * Получение объекта с изображением и областями
 * @param {number} objectId - ID объекта
 */
export const fetchObjectDetails = (objectId) => async (dispatch, getState) => {
  await dispatch(checkAndUpdateToken());
  if (!getState().auth.token) return null;

  try {
    const response = await fetch(
      `${BACKEND_URL_PREFIX}/info_kiosk/scheme/objects/${objectId}`,
      buildDefaultHeaders(getState)
    );

    if (response.ok) {
      return await response.json();
    } else {
      return null;
    }
  } catch (err) {
    console.error('Ошибка загрузки деталей объекта:', err);
    return null;
  }
};
